<template>
  <!-- Card-based redesign of the TEPROSIF form for the Corrección de Tests
       chatbot. Same two-step, server-driven flow as Teprosif.vue (step 0 =
       Registro / emissions, step 1 = Corrección / process selects) and the
       exact same `answer.answers` payload — only the presentation changes.
       This is a deliberate fork: ModalAutomaticTest keeps using Teprosif.vue. -->
  <div class="tep-root">
    <!-- ── HEADER ── -->
    <div class="tep-hdr">
      <div class="tep-hdr-row">
        <div class="tep-logo-pill">
          <div class="tep-logo-gem">{{ answer.step === 0 ? "🔤" : "🔬" }}</div>
          <span class="tep-logo-name">TEPROSIF</span>
        </div>
        <div class="tep-hdr-spacer"></div>
        <div class="tep-hdr-step">
          {{ answer.step === 0 ? "Paso 1 · Registro" : "Paso 2 · Revisión" }}
        </div>
      </div>
      <div class="tep-prog-wrap">
        <div
          class="tep-prog-fill"
          :class="answer.step === 0 ? 'p1' : 'p2'"
          :style="{ width: answer.step === 0 ? progress + '%' : '100%' }"
        ></div>
      </div>
    </div>

    <!-- ══════════ STEP 0 — REGISTRO ══════════ -->
    <template v-if="answer.step === 0">
      <div class="tep-phase-banner">
        <span class="tep-pdot" style="background: var(--g)"></span>
        Escribe la emisión de cada ítem · luego toca Corregir procesos
      </div>

      <div class="tep-main">
        <div class="tep-instruc">
          <strong>Instrucciones:</strong> Escribe verbatim la emisión del
          niño/a (ej: <em>temefono</em>). Si pronunció bien, deja vacío. Si no
          respondió, marca <strong>Omitida</strong>. /r/ inicial →
          <strong>-r</strong> · Yod → <strong>i</strong> · Wau →
          <strong>w</strong>
        </div>

        <!-- config: edad + dislalias -->
        <div class="tep-config">
          <div class="tep-config-row">
            <label class="tep-config-lbl">Edad</label>
            <select v-model="answer.answers.edad" class="tep-edad-sel">
              <option :value="undefined" disabled>Selecciona…</option>
              <option v-for="e in test.edad" :key="e.value" :value="e.value">
                {{ e.text }}
              </option>
            </select>
          </div>
          <p v-if="!all_input" class="tep-edad-warning">
            Debes completar la edad
          </p>
          <p class="tep-config-help">
            Selecciona los fonemas que deben considerarse dislalias (el programa
            también las detecta si un fonema se omite de forma consistente).
          </p>
          <div class="tep-dislalias">
            <button
              v-for="fonema in test.dislalias"
              :key="fonema.value"
              type="button"
              class="tep-dchip"
              :class="{ active: isSelected(fonema.value) }"
              @click="toggleFonema(fonema.value)"
            >
              {{ fonema.text }}
            </button>
          </div>
        </div>

        <!-- item cards -->
        <div
          v-for="(item, index) in test.items"
          :key="index"
          class="tep-icard"
          :class="{ omitted: isOmitted(item) }"
        >
          <div
            class="tep-icard-bar"
            :style="{ background: barColor(item) }"
          ></div>
          <div class="tep-icard-top">
            <div class="tep-ibadge">{{ index + 1 }}</div>
            <div class="tep-iword">{{ item }}</div>
            <button
              type="button"
              class="tep-omit-btn"
              :class="{ active: isOmitted(item) }"
              @click="toggleOmit(item)"
            >
              🚫 Omitida
            </button>
          </div>
          <div
            class="tep-em-wrap"
            :style="omittedStyle(item)"
          >
            <span class="tep-em-arr">→</span>
            <span class="tep-em-echo">{{ item.toLowerCase() }}</span>
            <input
              class="tep-em-input"
              type="text"
              v-model="answer.answers[item]"
              :disabled="isOmitted(item)"
              placeholder="emisión del niño…"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
            />
          </div>
          <div
            class="tep-em-line"
            :class="{ filled: hasEmission(item) }"
            :style="{ opacity: isOmitted(item) ? 0.3 : 1 }"
          ></div>
          <div class="tep-sug-row" v-if="!isOmitted(item)">
            <span
              v-for="(s, si) in suggestions(item)"
              :key="si"
              class="tep-sug-chip"
              >💡 {{ s }}</span
            >
          </div>
        </div>
      </div>

      <div class="tep-fab-wrap">
        <button
          class="tep-fab"
          :disabled="!all_input"
          @click="sendInitialTest"
        >
          <div class="tep-fab-l">
            <div class="tep-fab-icon">✏️</div>
            <div>
              <div>Corregir procesos</div>
              <div class="tep-fab-sub">{{ registeredCount }} de 37 registrados</div>
            </div>
          </div>
          <div class="tep-fab-arr">→</div>
        </button>
      </div>
    </template>

    <!-- ══════════ STEP 1 — CORRECCIÓN ══════════ -->
    <template v-else>
      <div class="tep-phase-banner">
        <span class="tep-pdot" style="background: var(--y)"></span>
        Ajusta los procesos detectados · toca el código para cambiarlo
      </div>

      <div class="tep-main">
        <div class="tep-instruc">
          Procesos detectados <strong>automáticamente</strong>. Cada fila tiene
          3 slots (Estructural · Asimilación · Sustitución): toca un selector
          para cambiar el código.
        </div>

        <div
          v-for="(item, index) in test.items"
          :key="index"
          class="tep-icard"
          :class="{ omitted: isOmitted(item) }"
        >
          <div
            class="tep-icard-bar"
            :style="{ background: barColor(item) }"
          ></div>
          <div class="tep-icard-top">
            <div class="tep-ibadge">{{ index + 1 }}</div>
            <div style="flex: 1; min-width: 0">
              <div class="tep-iword" style="font-size: 16px">{{ item }}</div>
              <div v-if="isOmitted(item)" class="tep-emit-omit">Omitida</div>
              <div v-else class="tep-emit-readout">
                {{ answer.answers[item] }}
              </div>
            </div>
          </div>

          <div class="tep-proc-rows" v-if="!isOmitted(item)">
            <!-- EST -->
            <div class="tep-proc-row">
              <span class="tep-proc-dot est"></span>
              <select
                v-for="suf in ['EA', 'EB', 'EC']"
                :key="suf"
                class="tep-proc-sel"
                :class="selClass(answer.answers[item + suf])"
                v-model="answer.answers[item + suf]"
                :title="test.selec_info[item + suf]"
              >
                <option value="-">—</option>
                <option v-for="o in OPTS_EST" :key="o" :value="o">{{ o }}</option>
              </select>
            </div>
            <!-- ASIM -->
            <div class="tep-proc-row">
              <span class="tep-proc-dot asim"></span>
              <select
                v-for="suf in ['AA', 'AB', 'AC']"
                :key="suf"
                class="tep-proc-sel"
                :class="selClass(answer.answers[item + suf])"
                v-model="answer.answers[item + suf]"
                :title="test.selec_info[item + suf]"
              >
                <option value="-">—</option>
                <option v-for="o in OPTS_ASIM" :key="o" :value="o">{{ o }}</option>
              </select>
            </div>
            <!-- SUST -->
            <div class="tep-proc-row">
              <span class="tep-proc-dot sust"></span>
              <select
                v-for="suf in ['SA', 'SB', 'SC']"
                :key="suf"
                class="tep-proc-sel"
                :class="selClass(answer.answers[item + suf])"
                v-model="answer.answers[item + suf]"
                :title="test.selec_info[item + suf]"
              >
                <option value="-">—</option>
                <option v-for="o in OPTS_SUST" :key="o" :value="o">{{ o }}</option>
              </select>
            </div>
          </div>

          <div class="tep-icard-foot" v-if="!isOmitted(item)">
            <div class="tep-foot-tags">
              <span
                v-for="(c, ci) in activeCodes(item)"
                :key="ci"
                class="tep-ftag"
                :class="codeType(c)"
                >{{ c }}</span
              >
            </div>
            <div class="tep-foot-total">
              Total
              <span class="tep-foot-num">{{ countProcs(item) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="tep-fab-wrap">
        <button class="tep-fab" @click="sendTest">
          <div class="tep-fab-l">
            <div class="tep-fab-icon">📋</div>
            <div>
              <div>Finalizar y corregir test</div>
              <div class="tep-fab-sub">{{ totalProcs }} procesos marcados</div>
            </div>
          </div>
          <div class="tep-fab-arr">→</div>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import testService from "../../../../services/testService";
import redpieService from "../../../../services/redpieService";

export default {
  name: "TeprosifChatbot",
  props: ["test_id", "student_id", "previous_answer", "semester"],
  mounted() {
    this.ensureFonts();
    if (this.previous_answer && Object.keys(this.previous_answer).length) {
      for (const [key, value] of Object.entries(
        this.previous_answer["step-0"]
      )) {
        this.$set(this.answer.answers, key, value);
      }
    }
  },
  watch: {
    student_id() {
      this.answer.student_id = this.student_id;
    },
    test_id() {
      this.answer.test = this.test_id;
    },
  },
  data() {
    return {
      // Process-code option lists (codes only — the select shows just the
      // code, the description rides along in the option's title via
      // test.selec_info). Mirrors Teprosif.vue.
      OPTS_EST: ["E1", "E2", "E3", "E4", "E5", "E6", "E7", "E8"],
      OPTS_ASIM: ["A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9"],
      OPTS_SUST: [
        "S1", "S2", "S3", "S4", "S5", "S6", "S7", "S8",
        "S9", "S10", "S11", "S12", "S13", "S14", "S15", "S16",
      ],
      // Visual-only "Omitida" markers, keyed by item. Not part of the payload
      // (the backend/RedPsi has no omitted field) — toggling it just dims the
      // card and disables that item's emission input.
      omitted: {},
      answer: {
        test: this.test_id,
        answers: { dislalias: [] },
        student_id: this.student_id,
        step: 0,
      },
      test: {
        selec_info: {},
        total: {},
        edad: [
          { value: "3", text: "3.0 - 3.11 años" },
          { value: "4", text: "4.0 - 4.11 años" },
          { value: "5", text: "5.0 - 5.11 años" },
          { value: "6", text: "6.0 - 6.11 años" },
        ],
        dislalias: [
          { value: "a", text: "a" },
          { value: "b", text: "b" },
          { value: "C", text: "ch" },
          { value: "d", text: "d" },
          { value: "e", text: "e" },
          { value: "f", text: "f" },
          { value: "g", text: "g" },
          { value: "i", text: "i" },
          { value: "j", text: "j" },
          { value: "k", text: "k" },
          { value: "l", text: "l" },
          { value: "L", text: "ll" },
          { value: "m", text: "m" },
          { value: "n", text: "n" },
          { value: "ñ", text: "ṉ (ñ)" },
          { value: "o", text: "o" },
          { value: "p", text: "p" },
          { value: "r", text: "r" },
          { value: "R", text: "rr" },
          { value: "s", text: "s" },
          { value: "t", text: "t" },
          { value: "u", text: "u" },
          { value: "w", text: "w" },
        ],
        items: [
          "Plancha", "Rueda", "Mariposa", "Bicicleta", "Helicoptero",
          "Bufanda", "Caperucita", "Alfombra", "Refrigerador", "Edificio",
          "Calcetin", "Dinosaurio", "Telefono", "Remedio", "Peineta", "Auto",
          "Indio", "Pantalon", "Camion", "Cuaderno", "Micro", "Tren",
          "Platano", "Jugo", "Enchufe", "Jabon", "Tambor", "Volantin",
          "Jirafa", "Gorro", "Arbol", "Dulce", "Guitarra", "Guante", "Reloj",
          "Jaula", "Puente",
        ],
      },
    };
  },
  computed: {
    all_input() {
      return !!this.answer.answers.edad;
    },
    registeredCount() {
      return this.test.items.filter(
        (it) => this.isOmitted(it) || (this.answer.answers[it] || "").trim()
      ).length;
    },
    progress() {
      return Math.round((this.registeredCount / this.test.items.length) * 100);
    },
    totalProcs() {
      return this.test.items.reduce((s, it) => s + this.countProcs(it), 0);
    },
  },
  methods: {
    // Load the Sora / JetBrains Mono webfonts once (the design relies on them;
    // injected programmatically to avoid a remote @import in the bundle).
    ensureFonts() {
      if (document.getElementById("tep-fonts")) return;
      const link = document.createElement("link");
      link.id = "tep-fonts";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
      document.head.appendChild(link);
    },
    // ── display helpers ──────────────────────────────────────────────
    isOmitted(item) {
      return !!this.omitted[item];
    },
    hasEmission(item) {
      return !!(this.answer.answers[item] || "").trim();
    },
    omittedStyle(item) {
      return this.isOmitted(item)
        ? { opacity: 0.3, pointerEvents: "none" }
        : {};
    },
    barColor(item) {
      if (this.isOmitted(item)) return "var(--r)";
      if (this.answer.step === 1) {
        return this.countProcs(item) > 0 ? "var(--y)" : "var(--g)";
      }
      return this.hasEmission(item) ? "var(--g)" : "transparent";
    },
    codeType(code) {
      if (!code) return "";
      return code[0] === "E" ? "est" : code[0] === "A" ? "asim" : "sust";
    },
    selClass(val) {
      if (!val || val === "-") return "";
      return "has-" + this.codeType(val);
    },
    activeCodes(item) {
      const sufs = ["EA", "EB", "EC", "AA", "AB", "AC", "SA", "SB", "SC"];
      return sufs
        .map((s) => this.answer.answers[item + s])
        .filter((v) => v && v !== "-");
    },
    countProcs(item) {
      return this.activeCodes(item).length;
    },
    // Lightweight client-side hints (visual only — the authoritative
    // detection happens server-side at step-0 submit). Ported from the
    // mockup: flags syllable reduction and onset omission.
    norm(s) {
      return (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim();
    },
    vowels(s) {
      return ((s || "").toLowerCase().match(/[aeiouáéíóúü]/g) || []).length;
    },
    suggestions(item) {
      const val = (this.answer.answers[item] || "").trim();
      if (!val) return [];
      const out = [];
      const ts = this.vowels(this.norm(item)) || 1;
      const es = this.vowels(this.norm(val)) || 1;
      if (es < ts) out.push(`Red. silábica ${ts}→${es}`);
      if (
        /^[bcdfgjklmnpqrstvwxyz]/.test(this.norm(item)) &&
        /^[aeiouáéíóú]/.test(this.norm(val))
      ) {
        out.push("Omisión onset");
      }
      return out;
    },
    // ── interactions ─────────────────────────────────────────────────
    toggleOmit(item) {
      this.$set(this.omitted, item, !this.omitted[item]);
      if (this.omitted[item]) {
        // Clear the typed emission so the dimmed card reads as empty.
        this.$set(this.answer.answers, item, "");
      }
    },
    isSelected(value) {
      return this.answer.answers.dislalias.includes(value);
    },
    toggleFonema(value) {
      const index = this.answer.answers.dislalias.indexOf(value);
      if (index === -1) {
        this.answer.answers.dislalias.push(value);
      } else {
        this.answer.answers.dislalias.splice(index, 1);
      }
    },
    // ── submit (identical payload contract to Teprosif.vue) ───────────
    getDefaultAnswers() {
      const defaultAnswers = {};
      this.test.items.forEach((item) => {
        defaultAnswers[item] = item;
      });
      return defaultAnswers;
    },
    sendInitialTest() {
      const defaultAnswers = this.getDefaultAnswers();
      Object.keys(defaultAnswers).forEach((key) => {
        if (!this.answer.answers[key]) {
          this.$set(this.answer.answers, key, defaultAnswers[key]);
        }
      });
      this.answer["semester"] = this.semester;
      testService.send_test(this.answer).then(
        (data) => {
          this.answer.redpsi_id = data.redpsi_id;
          this.answer.answers = { ...this.answer.answers, ...data.selec };
          // Ensure every process slot exists reactively so v-model writes in
          // step 1 keep the live footer tags/totals in sync (keys absent from
          // data.selec would otherwise be set non-reactively in Vue 2).
          const sufs = ["EA", "EB", "EC", "AA", "AB", "AC", "SA", "SB", "SC"];
          this.test.items.forEach((item) => {
            sufs.forEach((s) => {
              if (this.answer.answers[item + s] === undefined) {
                this.$set(this.answer.answers, item + s, "-");
              }
            });
          });
          this.test.selec_info = data.selec_info;
          this.test.total = data.total;
          this.answer.step = 1;
          this.answer.previous_answer = data.data;
          const msg = `Ya casi estamos! Pincha en algún proceso si quieres modificarlo, para terminar presiona 'Finalizar y corregir test'`;
          this.$bvModal.msgBoxOk(msg, {
            okTitle: "ENTENDIDO",
            centered: true,
            size: "lg",
          });
        },
        () => {
          this.$toasted.error("Ha ocurrido al hacer el test");
        }
      );
    },
    sendTest() {
      const defaultAnswers = this.getDefaultAnswers();
      Object.keys(defaultAnswers).forEach((key) => {
        if (!this.answer.answers[key]) {
          this.$set(this.answer.answers, key, defaultAnswers[key]);
        }
      });
      this.answer["semester"] = this.semester;
      testService
        .send_test(this.answer)
        .then((data) => {
          this.$emit("add-test", data);
          redpieService
            .assignDocuments({
              test: this.answer.test,
              school: this.$store.state.login.school,
              year: this.$store.state.login.year,
              student_grade: this.answer.student_id,
            })
            .then(() => {
              this.$emit("update-document");
            })
            .catch(() => {
              this.$toasted.error("Ha ocurrido al crear el documento");
            });
        })
        .catch(() => {
          this.$toasted.error("Ha ocurrido al hacer el test");
        });
    },
  },
};
</script>

<style scoped>
.tep-root {
  --y: #f8ac04;
  --yll: #fef7e2;
  --yd: #7a5500;
  --g: #20b2aa;
  --gll: #e2f7f5;
  --gd: #0d6b65;
  --m: #c6588d;
  --mll: #faeef6;
  --md: #7c2255;
  --r: #e84756;
  --rll: #fdedef;
  --ink: #111827;
  --mu: #6b7280;
  --mu2: #9ca3af;
  --mu3: #d1d5db;
  --border: rgba(0, 0, 0, 0.08);
  --surf: #fff;
  --bg: #f0f2f6;
  --rad: 14px;
  --rad-sm: 9px;
  --font: "Sora", sans-serif;
  --mono: "JetBrains Mono", monospace;
  --sh: 0 1px 6px rgba(0, 0, 0, 0.07), 0 0 0 0.5px rgba(0, 0, 0, 0.05);
  max-width: 560px;
  margin: 0 auto;
  font-family: var(--font);
  color: var(--ink);
  background: var(--bg);
  border-radius: var(--rad);
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.tep-root *,
.tep-root *::before,
.tep-root *::after {
  box-sizing: border-box;
}

/* ── HEADER ── */
.tep-hdr {
  background: var(--ink);
  padding: 13px 16px 11px;
}
.tep-hdr-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.tep-logo-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 0.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 30px;
  padding: 4px 10px 4px 6px;
}
.tep-logo-gem {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--g), var(--y));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.tep-logo-name {
  font-size: 13px;
  font-weight: 600;
  color: white;
  letter-spacing: -0.01em;
}
.tep-hdr-spacer {
  flex: 1;
}
.tep-hdr-step {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
}
.tep-prog-wrap {
  height: 2.5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}
.tep-prog-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.p1 {
  background: linear-gradient(90deg, var(--g), #84cec7);
}
.p2 {
  background: linear-gradient(90deg, var(--y), #f9d38f);
}

/* ── PHASE BANNER ── */
.tep-phase-banner {
  display: flex;
  align-items: center;
  gap: 7px;
  background: var(--surf);
  border-bottom: 0.5px solid var(--border);
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 500;
  color: var(--mu);
}
.tep-pdot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── MAIN ── */
.tep-main {
  padding: 12px 14px;
}
.tep-instruc {
  background: var(--surf);
  border-radius: var(--rad-sm);
  border-left: 3px solid var(--g);
  padding: 9px 12px;
  margin-bottom: 12px;
  font-size: 11px;
  color: var(--mu);
  line-height: 1.55;
}
.tep-instruc strong {
  color: var(--ink);
  font-weight: 600;
}

/* ── CONFIG (edad + dislalias) ── */
.tep-config {
  background: var(--surf);
  border-radius: var(--rad);
  border: 0.5px solid var(--border);
  box-shadow: var(--sh);
  padding: 12px;
  margin-bottom: 12px;
}
.tep-config-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tep-config-lbl {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}
.tep-edad-sel {
  flex: 1;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--mu3);
  background: white;
  font-family: var(--font);
  font-size: 13px;
  color: var(--ink);
  padding: 0 10px;
  outline: none;
  cursor: pointer;
}
.tep-edad-warning {
  color: var(--r);
  font-size: 11px;
  font-weight: 600;
  margin: 6px 0 0;
}
.tep-config-help {
  font-size: 10px;
  color: var(--mu);
  line-height: 1.5;
  margin: 10px 0 8px;
}
.tep-dislalias {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.tep-dchip {
  min-width: 30px;
  padding: 4px 9px;
  border-radius: 8px;
  border: 1px solid var(--mu3);
  background: var(--bg);
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 600;
  color: var(--mu);
  cursor: pointer;
  transition: all 0.12s;
}
.tep-dchip.active {
  background: var(--gll);
  border-color: var(--g);
  color: var(--gd);
}

/* ── ITEM CARD ── */
.tep-icard {
  background: var(--surf);
  border-radius: var(--rad);
  box-shadow: var(--sh);
  margin-bottom: 8px;
  border: 0.5px solid var(--border);
  overflow: hidden;
}
.tep-icard.omitted {
  opacity: 0.55;
}
.tep-icard-bar {
  height: 3px;
  background: transparent;
  transition: background 0.25s;
}
.tep-icard-top {
  display: flex;
  align-items: center;
  padding: 10px 12px 0;
  gap: 8px;
}
.tep-ibadge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--ink);
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tep-iword {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.025em;
  flex: 1;
}
.tep-omit-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--mu3);
  background: var(--bg);
  font-family: var(--font);
  font-size: 11px;
  font-weight: 600;
  color: var(--mu);
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.tep-omit-btn.active {
  background: var(--rll);
  border-color: var(--r);
  color: var(--r);
}

/* emission row */
.tep-em-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
}
.tep-em-arr {
  font-size: 11px;
  color: var(--mu3);
  flex-shrink: 0;
}
.tep-em-echo {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--mu3);
  text-decoration: line-through;
  text-decoration-color: rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
.tep-em-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: var(--mono);
  font-size: 15px;
  color: var(--g);
  font-weight: 500;
  outline: none;
  min-width: 0;
}
.tep-em-input::placeholder {
  color: var(--mu3);
  font-style: italic;
  font-weight: 400;
  font-family: var(--font);
  font-size: 12px;
}
.tep-em-line {
  height: 1px;
  background: var(--mu3);
  margin: 0 12px 8px;
  transition: background 0.15s;
}
.tep-em-line.filled {
  background: var(--g);
}
.tep-sug-row {
  padding: 0 12px 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.tep-sug-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 20px;
  background: var(--yll);
  border: 0.5px solid var(--y);
  color: var(--yd);
  font-size: 10px;
  font-weight: 600;
}

/* step-1 emission readout */
.tep-emit-readout {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--g);
  font-weight: 500;
}
.tep-emit-omit {
  font-size: 10px;
  color: var(--r);
  font-weight: 500;
}

/* ── PROC ROWS (step 1) ── */
.tep-proc-rows {
  border-top: 0.5px solid var(--border);
  padding: 8px 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: #fafbfc;
  margin-top: 8px;
}
.tep-proc-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tep-proc-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tep-proc-dot.est {
  background: var(--y);
}
.tep-proc-dot.asim {
  background: var(--g);
}
.tep-proc-dot.sust {
  background: var(--m);
}
.tep-proc-sel {
  flex: 1;
  height: 30px;
  min-width: 0;
  border-radius: 7px;
  border: 1px solid var(--mu3);
  background: white;
  font-family: var(--mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--mu2);
  padding: 0 16px 0 6px;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%23d1d5db' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 4px center;
  transition: border-color 0.12s, background-color 0.12s, color 0.12s;
  text-overflow: ellipsis;
}
.tep-proc-sel.has-est {
  background-color: var(--yll);
  border-color: var(--y);
  color: var(--yd);
}
.tep-proc-sel.has-asim {
  background-color: var(--gll);
  border-color: var(--g);
  color: var(--gd);
}
.tep-proc-sel.has-sust {
  background-color: var(--mll);
  border-color: var(--m);
  color: var(--md);
}

/* footer tags */
.tep-icard-foot {
  padding: 6px 12px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 0.5px solid var(--border);
}
.tep-foot-tags {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  flex: 1;
}
.tep-ftag {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  font-family: var(--mono);
}
.tep-ftag.est {
  background: var(--yll);
  color: var(--yd);
}
.tep-ftag.asim {
  background: var(--gll);
  color: var(--gd);
}
.tep-ftag.sust {
  background: var(--mll);
  color: var(--md);
}
.tep-foot-total {
  font-size: 10px;
  color: var(--mu2);
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  margin-left: 6px;
}
.tep-foot-num {
  font-size: 16px;
  font-weight: 700;
  color: var(--g);
}

/* ── ACTION (FAB-styled, inline) ── */
.tep-fab-wrap {
  padding: 10px 14px 16px;
}
.tep-fab {
  width: 100%;
  padding: 14px 18px;
  border-radius: 13px;
  border: none;
  background: var(--ink);
  color: white;
  font-family: var(--font);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 20px rgba(17, 24, 39, 0.26);
  transition: transform 0.1s, opacity 0.15s;
  letter-spacing: -0.01em;
}
.tep-fab:active {
  transform: scale(0.99);
}
.tep-fab:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.tep-fab-l {
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
}
.tep-fab-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.13);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.tep-fab-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 400;
  margin-top: 1px;
}
.tep-fab-arr {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
</style>
